import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaPredicate } from 'react-media-hook';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import { fetchPortfolioSettingsByPropertyId } from 'reducers/portfolioSettingsReducer';
import {
	fetchPortfolio,
	getIsPropertyInPortfolio,
} from 'reducers/portfolioReducer';
import { removePropertyFromPortfolioSet } from 'data/reducers/portfoliosManagementReducer';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { showToast } from 'data/reducers/toastReducer';
import { PropertyFavoriteIcon } from 'view/components/PropertyFavoriteIcon';
import { getFavoriteProperties } from 'data/reducers/propertyManagementReducer';

import './AddToPortfolioButton.css';

const AddToPortfolioButton = (props) => {
	const dispatch = useDispatch();
	const isMobile = useMediaPredicate(MOBILE_SIZE);
	let {
		isLoading,
		userId,
		propertyData,
		isLoadingPortfolio,
		isRemovingProperty,
		isLoggedIn,
		propertyInPortfolio,
		isLoadingIsPropertyInPortfolio,
	} = useSelector((state) => ({
		isLoading: state.property.isLoading || state.address.isLoading,
		userId: state.user.userData?.id,
		propertyData: state.property.propertyData,
		isLoadingPortfolio:
			state.portfolio.isLoading ||
			state.portfolioSettings.isLoading ||
			state.portfolio.isLoadingIsPropertyInPortfolio,
		isRemovingProperty: state.portfolioSettings.isRemovingProperty,
		isLoggedIn: !!state.user.userData?.id,
		propertyInPortfolio: state.portfolio.propertyInPortfolio,
		isLoadingIsPropertyInPortfolio:
			state.portfolio.isLoadingIsPropertyInPortfolio,
	}));
	let isPropertyInPortfolio = !!propertyInPortfolio?.id;
	let isLoadingAnything =
		isLoading || isLoadingPortfolio || !propertyData?.id || isRemovingProperty;
	const [currentPropId, setCurrentPropId] = useState(null);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (isLoggedIn && propertyData?.id && propertyData.id !== currentPropId) {
			setCurrentPropId(propertyData?.id);
			if (!isLoadingIsPropertyInPortfolio) {
				dispatch(getIsPropertyInPortfolio(propertyData.id));
			}
		}
	}, [userId, isLoggedIn, propertyData?.id]);

	// Fetch favorite properties when user is logged in
	useEffect(() => {
		if (userId) {
			dispatch(getFavoriteProperties());
		}
	}, [userId, dispatch]);

	if (!userId) {
		// Logged out: send to sign-in, same as the Alerts card's "Sign Up To Get Alerts"
		return (
			<div className="d-inline-flex align-items-center">
				<Link className="btn portfolio-toggle-btn btn-add" to="/sign-in">
					{isMobile ? <i className="bi bi-plus-square" /> : 'Add to Portfolio'}
				</Link>
				<div className="overview-favorite-wrapper">
					<i className="bi bi-star text-muted" style={{ fontSize: '18px', cursor: 'pointer' }} />
				</div>
			</div>
		);
	}

	const handleRemoveProperty = (propertyId) => {
		// Open confirmation modal before removing property
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				color: 'danger',
				title: 'Remove Property',
				onConfirm: async () => {
					try {
						await dispatch(
							removePropertyFromPortfolioSet(
								propertyInPortfolio?.portfolio_set?.id,
								propertyInPortfolio?.id
							)
						);

						dispatch(fetchPortfolio(userId));
						dispatch(fetchPortfolioSettingsByPropertyId(propertyId));
						dispatch(getIsPropertyInPortfolio(propertyId));

						dispatch(
							showToast({
								title: 'Success!',
								body: 'Property has been removed from portfolio successfully.',
								type: 'success',
								duration: 3000,
							})
						);
					} catch (error) {
						console.error('Error removing property:', error);
						dispatch(
							showToast({
								title: 'Error!',
								body: 'Failed to remove property from portfolio. Please try again.',
								type: 'error',
								duration: 4000,
							})
						);
					}
				},
				children: (
					<div>
						Are you sure you want to remove &quot;
						{propertyData?.address || 'this property'}&quot; from your
						portfolio? This action cannot be undone.
					</div>
				),
				confirmLabel: 'Remove Property',
			})
		);
	};

	const handleAddProperty = (propertyId) => {
		dispatch(
			openModal(MODAL_TYPES.SELECT_PORTFOLIO, {
				propertyId: propertyId,
				onConfirm: () => {
					dispatch(fetchPortfolio(userId));
					dispatch(fetchPortfolioSettingsByPropertyId(propertyId));
					dispatch(getIsPropertyInPortfolio(propertyId));
				},
			})
		);
	};

	let addText = 'Add to Portfolio';
	let removeText = 'Remove From Portfolio';

	if (isLoadingAnything) {
		addText = 'Portfolio';
		removeText = 'Portfolio';
	}

	let togglePortfolioButton = (
		<button
			className="btn portfolio-toggle-btn btn-add"
			disabled={isLoadingAnything}
			onClick={() => handleAddProperty(propertyData.id)}
		>
			{isMobile ? <i className="bi bi-plus-square" /> : addText}
		</button>
	);

	if (!isLoadingPortfolio && isPropertyInPortfolio) {
		togglePortfolioButton = (
			<button
				className="btn portfolio-toggle-btn btn-remove"
				disabled={isLoadingAnything}
				onClick={() => handleRemoveProperty(propertyData.id)}
			>
				{isMobile ? <i className="bi bi-dash-square" /> : removeText}
			</button>
		);
	}

	return (
		<div className="d-inline-flex align-items-center">
			{togglePortfolioButton}
			{propertyData?.id && (
				<div className="overview-favorite-wrapper">
					<PropertyFavoriteIcon
						propertyId={propertyData?.id}
						property={propertyData}
					/>
				</div>
			)}
		</div>
	);
};

export default AddToPortfolioButton;
